<template>
	<div>
		<v-alert v-if="unacknowledgedPolicies.length > 0" :value="true" class="text-xs-left mb-4" icon="error" color="error">
			{{ unacknowledgedPoliciesWarningText }}
		</v-alert>

		<v-card>
			<v-toolbar flat class="growing-toolbar">
				<v-layout row wrap align-center>
					<v-flex>
						<v-select class="mr-2" v-model="categoryFilter" :items="categoryOptions" item-text="name" item-value="id" label="Filter by Category" no-data-text="No categories have been assigned" clearable />
					</v-flex>

					<v-flex>
						<v-checkbox class="pt-3" label="Only show policies that are relevant to me" v-model="relevantOnly"></v-checkbox>
					</v-flex>

					<v-flex>
						<v-text-field v-model="search" append-icon="search" label="Search" single-line hide-details background-color="#fff" class="mb-1" autofocus />
					</v-flex>
				</v-layout>
			</v-toolbar>

			<v-data-table :headers="headers" :items="filteredPolicies" :search="search" hide-actions class="elevation-1">
				<template slot="items" slot-scope="props">
					<tr style="height: 64px; cursor: pointer;" @click="viewDocument(props.item.id)">
						<td class="text-xs-left">
							<v-layout row>
								<v-flex shrink>
									<v-list-tile-avatar style="display: inline; width: 32px; height: 32px;" class="mr-3">
										<v-icon v-if="props.item.agreedData.agreed" class="success white--text">assignment_turned_in</v-icon>
										<v-icon v-else-if="props.item.isRelevantToEmployee" class="error white--text">assignment_late</v-icon>
										<v-icon v-else class="grey white--text">assignment</v-icon>
									</v-list-tile-avatar>
								</v-flex>
								<v-flex style="align-self: center;">
									<router-link :to="{ name: 'hrDocument', params: { id: props.item.id } }">{{ props.item.name }}</router-link>
									<div v-if="$_hasAccessToFeature($FeatureId.COMPANY_POLICIES_MANAGE, $PermissionAction.USE) && props.item.newVersionAvailable" class="info-text mt-1">
										<v-icon small color="info">mdi-cloud-download-outline</v-icon> There is a new version of this policy available for use.
									</div>
								</v-flex>
							</v-layout>
						</td>
						<td class="text-xs-left">
							<v-chip v-if="props.item.category" label small>{{ props.item.category.name }}</v-chip>
						</td>
						<td class="text-xs-left">
							<span v-if="props.item.agreedData.agreed">Acknowledged</span>
							<span v-else-if="props.item.isRelevantToEmployee" class="error--text">Requires Acknowledgement</span>
							<span v-else>Optional</span>
						</td>
						<td style="width: 1px;">{{ props.item.versionNo }}</td>
						<td class="text-xs-left">
							<AppDate :date="props.item.agreedData.agreementDateUtc" show-date format="Do MMM YYYY, HH:mm" />
						</td>
						<td class="justify-center px-0">
							<v-icon v-if="$_hasAccessToFeature($FeatureId.COMPANY_POLICIES_MANAGE, $PermissionAction.USE) && props.item.contentType == 0" small class="mr-2" @click.stop="editDocument(props.item.id)">
								edit
							</v-icon>
							<v-icon v-if="$_hasAccessToFeature($FeatureId.COMPANY_POLICIES_MANAGE, $PermissionAction.USE)" small @click.stop="deleteDocument(props.item)">
								delete
							</v-icon>
						</td>
					</tr>
				</template>
				<template v-slot:no-results>
					<v-alert :value="true" color="error" outline icon="warning"> Your search for "{{ search }}" found no results. </v-alert>
				</template>
			</v-data-table>
		</v-card>
	</div>
</template>

<script>
	export default {
		props: {
			hrDocuments: {
				type: Array,
				required: true
			},
			isAdmin: {
				type: Boolean,
				required: true
			}
		},
		data() {
			return {
				search: null,
				headers: [
					{ text: 'Policy Name', value: 'name' },
					{ text: 'Category', value: 'category' },
					{ text: 'Status', value: 'status' },
					{ text: 'Document Version', value: 'version' },
					{ text: 'Acknowledged at', value: 'acknowledged', sortable: false },
					{ text: '', value: 'actions', sortable: false }
				],
				relevantOnly: true,
				categoryFilter: null
			};
		},
		computed: {
			categoryOptions() {
				const unique = [];
				this.hrDocuments.forEach(doc => {
					if (doc.category && doc.category.id && !unique.find(x => x.id === doc.category.id)) {
						unique.push(doc.category);
					}
				});

				return unique.sort((a, b) => a.name.localeCompare(b.name));
			},
			unacknowledgedPolicies() {
				return this.hrDocuments.filter(x => x.isRelevantToEmployee && !x.agreedData.agreed);
			},
			unacknowledgedPoliciesWarningText() {
				if (this.unacknowledgedPolicies.length > 1) {
					return `You have ${this.unacknowledgedPolicies.length} policies that require your attention.`;
				} else {
					return `You have 1 policy that requires your attention.`;
				}
			},
			filteredPolicies() {
				let docs = this.hrDocuments;

				if (this.relevantOnly === true) {
					docs = docs.filter(x => x.isRelevantToEmployee);
				}

				if (this.categoryFilter) {
					docs = docs.filter(x => x.category && x.category.id === this.categoryFilter);
				}

				return docs;
			}
		},
		methods: {
			viewDocument(hrDocumentId) {
				this.$router.push(`/hrDocuments/${hrDocumentId}`);
			},
			editDocument(hrDocumentId) {
				this.$router.push(`/hrDocuments/${hrDocumentId}/edit`);
			},
			getLastUpdatedText(hrDocument) {
				if (hrDocument) {
					if (hrDocument.lastUpdatedDateUTC !== null) {
						return `Last Updated ${this.$options.filters.formatDateTime(hrDocument.lastUpdatedDateUTC)}`;
					} else {
						return `Created ${this.$options.filters.formatDateTime(hrDocument.createdDateUTC)}`;
					}
				}
				return null;
			},
			deleteDocument(hrDocument) {
				const index = this.hrDocuments.indexOf(hrDocument);
				if (confirm('Are you sure you want to delete this policy?')) {
					this.$store.dispatch('hrDocuments/delete', hrDocument).then(
						response => {},
						error => {
							console.error('Error deleting HR Document');
						}
					);
				}
			}
		}
	};
</script>

<style scoped>
	.info-text {
		color: rgba(0, 0, 0, 0.54);
		font-size: 12px;
	}
	.growing-toolbar >>> .v-toolbar__content {
		height: auto !important;
		min-height: 64px;
		align-items: flex-start;
		padding-top: 8px;
		padding-bottom: 8px;
		flex-wrap: wrap;
	}
</style>
